import React, { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import logo from '../image/logo.png';
import { PHOTO_TYPES, PortalScheduleSlot } from '../../../config/portalClient';
import '../EstudianteDashboard.css';

export const Empty: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="est-panel est-empty">{children}</div>
);

export const IconHorario = () => (
  <svg viewBox="0 0 64 64" aria-hidden="true">
    <rect x="8" y="12" width="44" height="40" rx="5" fill="#e2e8f0" />
    <rect x="8" y="12" width="44" height="11" rx="5" fill="#f43f5e" />
    <rect x="17" y="7" width="4" height="10" rx="2" fill="#475569" />
    <rect x="39" y="7" width="4" height="10" rx="2" fill="#475569" />
    {[0, 1, 2, 3].map(c => [0, 1, 2].map(r => (
      <rect key={`${c}-${r}`} x={14 + c * 8} y={28 + r * 7} width="5" height="4" rx="1"
        fill={c === 1 && r === 1 ? '#0ea5e9' : '#94a3b8'} />
    )))}
    <circle cx="48" cy="46" r="11" fill="#1e293b" stroke="#e2e8f0" strokeWidth="2.5" />
    <path d="M48 40v6l4 3" stroke="#e2e8f0" strokeWidth="2.5" strokeLinecap="round" fill="none" />
  </svg>
);

export const IconCalificaciones = () => (
  <svg viewBox="0 0 64 64" aria-hidden="true">
    <rect x="12" y="10" width="40" height="48" rx="4" fill="#e2e8f0" />
    <rect x="24" y="6" width="16" height="8" rx="3" fill="#64748b" />
    {[0, 1, 2, 3, 4].map(r => (
      <g key={r}>
        <rect x="18" y={20 + r * 7} width="5" height="4" rx="1" fill="#94a3b8" />
        {Array.from({ length: 5 - r }).map((_, s) => (
          <path key={s} transform={`translate(${26 + s * 5} ${20 + r * 7})`}
            d="M2 0l.6 1.3 1.4.2-1 1 .2 1.4L2 3.2.8 3.9 1 2.5 0 1.5l1.4-.2z" fill="#f59e0b" />
        ))}
      </g>
    ))}
  </svg>
);

export const IconContrasena = () => (
  <svg viewBox="0 0 64 64" aria-hidden="true">
    <path d="M22 28v-7a10 10 0 0 1 20 0v7" stroke="#94a3b8" strokeWidth="5" fill="none" strokeLinecap="round" />
    <rect x="14" y="27" width="36" height="28" rx="5" fill="#f59e0b" />
    <circle cx="32" cy="38" r="4" fill="#78350f" />
    <rect x="30.5" y="40" width="3" height="8" rx="1.5" fill="#78350f" />
    <circle cx="49" cy="47" r="10" fill="#0ea5e9" />
    <path d="M44 47l3.5 3.5L54 44" stroke="#fff" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* ── Horario semanal ─────────────────────────────────────────────────────── */

export const WEEK_DAYS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
export const BLOCK_COLORS = ['#0ea5e9', '#22c55e', '#a855f7', '#f59e0b', '#f43f5e', '#14b8a6', '#6366f1', '#ec4899'];
const FIRST_HOUR = 6;
const LAST_HOUR = 21;
const HOURS = Array.from({ length: LAST_HOUR - FIRST_HOUR + 1 }, (_, i) => FIRST_HOUR + i);

export interface ScheduleSlot {
  day: number;
  start: number;
  end: number;
}

export interface ScheduleBlock {
  key: string;
  title: string;
  detail?: string | null;
  color: string;
  slots: ScheduleSlot[];
}

const DAY_PATTERNS: [RegExp, number][] = [
  [/\blun(es)?\b/, 0],
  [/\bmar(tes)?\b/, 1],
  [/\bmi[eé](rcoles)?\b/, 2],
  [/\bjue(ves)?\b/, 3],
  [/\bvie(rnes)?\b/, 4],
  [/\bs[aá]b(ado)?\b/, 5],
  [/\bdom(ingo)?\b/, 6],
];

const TIME_RANGE = /(\d{1,2})(?::(\d{2}))?\s*(a\.?\s?m\.?|p\.?\s?m\.?)?\s*(?:-|–|—|\ba\b|hasta)\s*(\d{1,2})(?::(\d{2}))?\s*(a\.?\s?m\.?|p\.?\s?m\.?)?/;

function toHours(hour: string, minutes: string | undefined, meridiem: string | undefined) {
  let h = Number(hour) % 24;
  const m = meridiem?.replace(/[\s.]/g, '');
  if (m === 'pm' && h < 12) h += 12;
  if (m === 'am' && h === 12) h = 0;
  return h + (minutes ? Number(minutes) / 60 : 0);
}

// El horario en texto libre de Siloe (p. ej. "Lunes y Miércoles 6:00 pm - 8:00 pm; Sábado 7-9").
function parseScheduleInfo(text: string | null | undefined): ScheduleSlot[] {
  if (!text) return [];
  const slots: ScheduleSlot[] = [];
  let pendingDays: number[] = [];
  for (const segment of text.toLowerCase().split(/[;\n/|]+/)) {
    const days = DAY_PATTERNS.filter(([re]) => re.test(segment)).map(([, d]) => d);
    if (days.length) pendingDays = days;
    const match = segment.match(TIME_RANGE);
    if (!match || !pendingDays.length) continue;
    const endMeridiem = match[6];
    let start = toHours(match[1], match[2], match[3] ?? endMeridiem);
    const end = toHours(match[4], match[5], endMeridiem ?? match[3]);
    if (start >= end && !match[3] && end >= 12) start = toHours(match[1], match[2], 'pm');
    if (end <= start) continue;
    pendingDays.forEach(day => slots.push({ day, start, end }));
  }
  return slots;
}

const DAY_CODE_INDEX: Record<PortalScheduleSlot['day'], number> = {
  MON: 0, TUE: 1, WED: 2, THU: 3, FRI: 4, SAT: 5, SUN: 6,
};

function clockToHours(value: string) {
  const [h, m] = value.split(':').map(Number);
  return h + (m || 0) / 60;
}

/** Bloques del horario: usa los bloques estructurados y, si no hay, interpreta el texto libre. */
export function scheduleSlots(slots: PortalScheduleSlot[] | undefined, text?: string | null): ScheduleSlot[] {
  if (slots?.length) {
    return slots
      .filter(slot => slot.day in DAY_CODE_INDEX)
      .map(slot => ({ day: DAY_CODE_INDEX[slot.day], start: clockToHours(slot.start), end: clockToHours(slot.end) }))
      .filter(slot => slot.end > slot.start);
  }
  return parseScheduleInfo(text);
}

function formatHour(h: number) {
  return `${String(h).padStart(2, '0')}:00 - ${String(h).padStart(2, '0')}:59`;
}

export const WeekSchedule: React.FC<{ blocks: ScheduleBlock[] }> = ({ blocks }) => {
  // grid[hora][día] = bloques que ocupan esa franja
  const grid = useMemo(() => {
    const cells: ScheduleBlock[][][] = HOURS.map(() => WEEK_DAYS.map(() => []));
    blocks.forEach(block => block.slots.forEach(slot => {
      HOURS.forEach((hour, row) => {
        if (slot.start < hour + 1 && slot.end > hour) cells[row][slot.day].push(block);
      });
    }));
    return cells;
  }, [blocks]);

  const showWeekend = grid.some(row => row[6].length > 0);
  const days = WEEK_DAYS.map((name, index) => ({ name, index })).filter(d => d.index < 6 || showWeekend);

  return (
    <div className="est-schedule-scroll">
      <table className="est-week">
        <thead>
          <tr>
            <th>Horas</th>
            {days.map(d => <th key={d.name}>{d.name}</th>)}
          </tr>
        </thead>
        <tbody>
          {HOURS.map((hour, row) => (
            <tr key={hour}>
              <td className="est-week-hour">{formatHour(hour)}</td>
              {days.map(d => {
                const cell = grid[row][d.index];
                const prev = row > 0 ? grid[row - 1][d.index] : [];
                if (cell.length === 1 && prev.length === 1 && prev[0] === cell[0]) return null;
                let span = 1;
                if (cell.length === 1) {
                  while (row + span < HOURS.length && grid[row + span][d.index].length === 1
                    && grid[row + span][d.index][0] === cell[0]) span += 1;
                }
                return (
                  <td key={d.name} rowSpan={span} className={cell.length ? 'has-class' : undefined}>
                    {cell.map(block => (
                      <div key={block.key} className="est-week-block" style={{ borderColor: block.color, background: `${block.color}22` }}>
                        <strong>{block.title}</strong>
                        {block.detail && <span>{block.detail}</span>}
                      </div>
                    ))}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const PrintButton: React.FC = () => (
  <button type="button" className="est-print" onClick={() => window.print()} aria-label="Imprimir horario">
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
      <path d="M19 8H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3zm-3 11H8v-5h8v5zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm-1-9H6v4h12V3z" />
    </svg>
  </button>
);

/* ── Encabezado institucional ────────────────────────────────────────────── */

interface PortalHeaderProps {
  homeTo: string;
  name: string | null;
  photo: string | null | undefined;
  meta: React.ReactNode[];
  ready: boolean;
  onUploadPhoto: (file: File) => Promise<string>;
  onPhotoChanged: (url: string) => void;
  onLogout: () => void;
}

export const PortalHeader: React.FC<PortalHeaderProps> = ({
  homeTo, name, photo, meta, ready, onUploadPhoto, onPhotoChanged, onLogout,
}) => {
  const photoInput = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handlePhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      onPhotoChanged(await onUploadPhoto(file));
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'No se pudo subir la foto.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <header className="est-header">
      <div className="est-header-inner">
        <Link to={homeTo} className="est-brand">
          <img src={logo} alt="CECAP" className="est-brand-logo" />
          <span className="est-brand-name">CECAP</span>
          <span className="est-brand-divider" aria-hidden="true" />
          <span className="est-brand-system">
            <span className="est-brand-system-top">Sistema de información</span>
            <span className="est-brand-system-main">Académico</span>
          </span>
        </Link>

        <div className="est-user">
          <div className="est-user-welcome">
            <span className="est-user-hello">¡Bienvenido!</span>
            <span className="est-user-name">{name ?? '…'}</span>
          </div>
          <span className="est-user-divider" aria-hidden="true" />
          <button
            type="button"
            className={`est-user-avatar${uploading ? ' is-busy' : ''}`}
            onClick={() => photoInput.current?.click()}
            disabled={!ready || uploading}
            title="Cambiar foto de perfil"
            aria-label="Cambiar foto de perfil"
          >
            {photo ? (
              <img src={photo} alt="" />
            ) : (
              <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="9" r="4" /><path d="M4 21c0-4.4 3.6-7 8-7s8 2.6 8 7z" /></svg>
            )}
            <span className="est-user-avatar-edit" aria-hidden="true">
              {uploading ? '…' : (
                <svg viewBox="0 0 24 24"><path d="M9 3 7.2 5H4a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-3.2L15 3H9zm3 14a4.5 4.5 0 1 1 0-9 4.5 4.5 0 0 1 0 9z" /></svg>
              )}
            </span>
          </button>
          <input
            ref={photoInput}
            type="file"
            accept={PHOTO_TYPES.join(',')}
            className="est-hidden-input"
            onChange={handlePhoto}
          />
          <div className="est-user-meta">
            {meta.map((line, i) => <span key={i}>{line}</span>)}
            <button type="button" className="est-user-logout" onClick={onLogout}>Cerrar sesión</button>
          </div>
        </div>
      </div>
      <div className="est-header-stripe" aria-hidden="true">
        <span /><span /><span /><span />
      </div>
    </header>
  );
};

/* ── Cambio de contraseña ────────────────────────────────────────────────── */

interface ChangePasswordFormProps {
  mustChangePassword: boolean;
  onPasswordChanged: () => void;
  changePassword: (current: string, next: string) => Promise<void>;
  forceChangePassword: (next: string) => Promise<void>;
}

export const ChangePasswordForm: React.FC<ChangePasswordFormProps> = ({
  mustChangePassword, onPasswordChanged, changePassword, forceChangePassword,
}) => {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ tone: 'error' | 'ok'; text: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (next.length < 8) {
      setMessage({ tone: 'error', text: 'La nueva contraseña debe tener al menos 8 caracteres.' });
      return;
    }
    if (next !== confirm) {
      setMessage({ tone: 'error', text: 'Las contraseñas nuevas no coinciden.' });
      return;
    }
    if (!mustChangePassword && next === current) {
      setMessage({ tone: 'error', text: 'La nueva contraseña debe ser distinta a la actual.' });
      return;
    }
    setSaving(true);
    try {
      if (mustChangePassword) {
        await forceChangePassword(next);
      } else {
        await changePassword(current, next);
      }
      setCurrent('');
      setNext('');
      setConfirm('');
      setMessage({ tone: 'ok', text: 'Tu contraseña se actualizó correctamente.' });
      onPasswordChanged();
    } catch (err) {
      setMessage({ tone: 'error', text: err instanceof Error ? err.message : 'No se pudo cambiar la contraseña.' });
    } finally {
      setSaving(false);
    }
  };

  const clear = () => setMessage(null);

  return (
    <form className="est-panel est-form" onSubmit={handleSubmit}>
      {mustChangePassword && (
        <div className="est-message est-message--error">
          Por seguridad, crea una contraseña nueva para reemplazar la temporal que te enviamos por correo.
        </div>
      )}
      {!mustChangePassword && (
        <label className="est-field">
          <span>Contraseña actual</span>
          <input type="password" value={current} onChange={e => { setCurrent(e.target.value); clear(); }}
            required autoComplete="current-password" />
        </label>
      )}
      <label className="est-field">
        <span>Nueva contraseña</span>
        <input type="password" value={next} onChange={e => { setNext(e.target.value); clear(); }}
          required autoComplete="new-password" />
      </label>
      <label className="est-field">
        <span>Confirmar nueva contraseña</span>
        <input type="password" value={confirm} onChange={e => { setConfirm(e.target.value); clear(); }}
          required autoComplete="new-password" />
      </label>
      {message && <div className={`est-message est-message--${message.tone}`}>{message.text}</div>}
      <div className="est-panel-actions">
        <button type="submit" className="est-btn" disabled={saving}>
          {saving ? 'Guardando...' : 'Guardar contraseña'}
        </button>
      </div>
    </form>
  );
};
